/**
 * Empty partners state component.
 *
 * Displays empty state message when no partners are found or while loading.
 * Provides actions to clear filters or create a new partner request.
 *
 * @module EmptyPartnersState
 */

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { usePartnerFinderPath } from '@/hooks/usePartnerFinderPath';
import { useTranslation } from 'react-i18next';

interface EmptyPartnersStateProps {
  readonly isLoading: boolean;
  readonly onClearFilters: () => void;
  readonly isMobile?: boolean;
}

/**
 * Component for displaying empty state when no partners are found or loading
 */
export function EmptyPartnersState({
  isLoading,
  onClearFilters,
  isMobile = false,
}: EmptyPartnersStateProps) {
  const navigate = useNavigate();
  const paths = usePartnerFinderPath();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Card className={isMobile ? 'p-8 text-center' : 'p-8 lg:p-12 text-center'}>
        <p className="text-muted-foreground">
          {t('partnerFinder.emptyStates.loadingPartners', { defaultValue: 'Loading partners...' })}
        </p>
      </Card>
    );
  }

  return (
    <Card className={isMobile ? 'p-8 text-center' : 'p-8 lg:p-12 text-center'}>
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">
          {t('partnerFinder.emptyStates.noPartners', { defaultValue: 'No partners found' })}
        </h3>
        <p className="text-muted-foreground">
          {t('partnerFinder.emptyStates.noPartnersDesc', {
            defaultValue: 'Try adjusting filters or create a request to be discovered.',
          })}
        </p>
        <div
          className={`flex ${
            isMobile ? 'flex-col gap-3' : 'flex-col sm:flex-row gap-3 justify-center'
          } pt-4`}
        >
          <Button variant="outline" onClick={onClearFilters}>
            {t('partnerFinder.buttons.clearFilters', { defaultValue: 'Clear filters' })}
          </Button>
          <Button onClick={() => navigate(paths.requestEdit)}>
            {t('partnerFinder.buttons.createRequest', { defaultValue: 'Create request' })}
          </Button>
        </div>
      </div>
    </Card>
  );
}
